import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { forwardRef } from 'react';
import addTestId from '../utils/addTestId';
import type { V3To } from '../utils/router/v3Bridge';
import { Link } from '../utils/router/v3Bridge';
import styles from './DropdownMenuItem.module.scss';

export type DropdownMenuItemType<Data> = {
  id: string;
  className?: string;
  link?: V3To;
  data: Data;
};

export type RenderItemFn<Data> = (
  data: Data,
  options: { id: string; focused?: boolean },
) => React.ReactNode;

interface DropdownMenuItemProps<Data>
  extends Pick<HTMLAttributes<HTMLDivElement>, 'onClick' | 'onMouseEnter'> {
  item: DropdownMenuItemType<Data>;
  focused?: boolean;
  renderItem: RenderItemFn<Data>;
}

const DropdownMenuItemInner = <Data,>(
  {
    item,
    focused,
    onClick,
    onMouseEnter,
    renderItem,
  }: DropdownMenuItemProps<Data>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const inner = renderItem(item.data, { id: item.id, focused });
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
    <div
      ref={ref}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={classNames(styles.item, item.className, {
        [styles.focused]: focused,
      })}
      {...addTestId('dropdownMenuItem')}
    >
      {item.link ? (
        <Link className={styles.link} to={item.link}>
          {inner}
        </Link>
      ) : (
        inner
      )}
    </div>
  );
};

const DropdownMenuItem = forwardRef(DropdownMenuItemInner) as <Data>(
  props: DropdownMenuItemProps<Data> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  },
) => ReturnType<typeof DropdownMenuItemInner>;

export default DropdownMenuItem;
